import React, { useCallback, useRef, useState, useSyncExternalStore } from 'react';
import { CropSelectionOutlineProps, ResizeDirectionEnum } from '../types';
import { getCursor } from './utils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

export const CropSelectionOutline: React.FC<CropSelectionOutlineProps> = ({
  facialBounds,
  personBounds,
  positions,
  updatePositions,
  aspectRatio,
  videoAspectRatio,
  maintainAspectRatio = true,
  minimumWidthPercentage = 0.05
}) => {
  const outlineRef = useRef<HTMLDivElement>(null);
  const maskRef = useRef<SVGRectElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const showPersonAndFaceCoordinates =
    featureFlags[FeatureFlagKeys.Use_CL_Show_Person_and_Face_Coordinates_in_Manual_Reposition];

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      setIsDragging(true);
      const dragStartPointerPosition = {
        x: e.clientX,
        y: e.clientY
      };
      let currentOutlinePosition = {
        top: positions.top,
        left: positions.left,
        width: positions.width,
        height: positions.height
      };

      const handleMouseMove = (e: MouseEvent) => {
        if (!outlineRef.current) {
          return;
        }
        const parentRect = outlineRef.current.parentElement?.getBoundingClientRect();
        if (!parentRect) {
          return;
        }

        const newLeft = Math.max(
          0,
          Math.min((e.clientX - dragStartPointerPosition.x) / parentRect.width + positions.left, 1 - positions.width)
        );
        const newTop = Math.max(
          0,
          Math.min((e.clientY - dragStartPointerPosition.y) / parentRect.height + positions.top, 1 - positions.height)
        );

        outlineRef.current.style.top = `${newTop * 100}%`;
        outlineRef.current.style.left = `${newLeft * 100}%`;
        outlineRef.current.style.width = `${positions.width * 100}%`;
        outlineRef.current.style.height = `${positions.height * 100}%`;

        maskRef.current?.setAttribute('x', `${newLeft * 100}%`);
        maskRef.current?.setAttribute('y', `${newTop * 100}%`);
        maskRef.current?.setAttribute('width', `${positions.width * 100}%`);
        maskRef.current?.setAttribute('height', `${positions.height * 100}%`);

        currentOutlinePosition = {
          top: newTop,
          left: newLeft,
          width: positions.width,
          height: positions.height
        };
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        const outline = outlineRef.current;
        if (!outline) {
          return;
        }
        const parentRect = outline.parentElement?.getBoundingClientRect();
        if (!parentRect) {
          return;
        }
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        updatePositions(currentOutlinePosition);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [positions.height, positions.left, positions.top, positions.width, updatePositions]
  );

  const handleResizeStart = (e: React.MouseEvent, direction: ResizeDirectionEnum) => {
    e.stopPropagation();
    const parentRect = outlineRef.current?.parentElement?.getBoundingClientRect();
    if (!parentRect) {
      return;
    }
    const initialPointerPosition = {
      x: e.clientX,
      y: e.clientY
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!outlineRef.current) {
        return;
      }

      const deltaX = e.clientX - initialPointerPosition.x;
      const deltaY = e.clientY - initialPointerPosition.y;

      const parentWidth = parentRect.width;
      const parentHeight = parentRect.height;

      const deltaXPercent = deltaX / parentWidth;
      const deltaYPercent = deltaY / parentHeight;

      let newWidth = positions.width;
      let newHeight = positions.height;
      let newLeft = positions.left;
      let newTop = positions.top;
      if (maintainAspectRatio) {
        if (direction === ResizeDirectionEnum.TOP_LEFT) {
          const widthChange = -deltaXPercent;
          newWidth = Math.min(Math.min(1, positions.width + widthChange), positions.left + positions.width);
          newHeight = (newWidth / aspectRatio) * videoAspectRatio;
          if (newHeight > positions.top + positions.height) {
            newHeight = positions.top + positions.height;
            newWidth = (newHeight * aspectRatio) / videoAspectRatio;
          }
          newLeft = positions.left + (positions.width - newWidth);
          newTop = positions.top + (positions.height - newHeight);
        } else if (direction === ResizeDirectionEnum.TOP_RIGHT) {
          const widthChange = deltaXPercent;
          newWidth = Math.min(Math.min(1, positions.width + widthChange), 1 - positions.left);
          newHeight = (newWidth / aspectRatio) * videoAspectRatio;
          if (newHeight > positions.top + positions.height) {
            newHeight = positions.top + positions.height;
            newWidth = (newHeight * aspectRatio) / videoAspectRatio;
          }
          newTop = positions.top + (positions.height - newHeight);
        } else if (direction === ResizeDirectionEnum.BOTTOM_RIGHT) {
          const widthChange = deltaXPercent;
          newWidth = Math.min(1, positions.width + widthChange, 1 - positions.left);
          newHeight = (newWidth / aspectRatio) * videoAspectRatio;
        } else if (direction === ResizeDirectionEnum.BOTTOM_LEFT) {
          const widthChange = -deltaXPercent;
          newWidth = Math.min(Math.min(1, positions.width + widthChange), positions.left + positions.width);
          newHeight = (newWidth / aspectRatio) * videoAspectRatio;
          newLeft = positions.left + (positions.width - newWidth);
        }
      } else {
        if (direction === ResizeDirectionEnum.TOP_LEFT || direction === ResizeDirectionEnum.BOTTOM_LEFT) {
          const maxDecrease = newWidth - minimumWidthPercentage;
          const actualDecrease = Math.min(deltaXPercent, maxDecrease);
          newWidth -= actualDecrease;
          newLeft += actualDecrease;
        }
        if (direction === ResizeDirectionEnum.TOP_RIGHT || direction === ResizeDirectionEnum.BOTTOM_RIGHT) {
          const maxIncrease = 1 - newLeft - newWidth;
          newWidth += Math.min(deltaXPercent, maxIncrease);
        }

        if (direction === ResizeDirectionEnum.TOP_LEFT || direction === ResizeDirectionEnum.TOP_RIGHT) {
          const maxDecrease = newHeight - minimumWidthPercentage;
          const actualDecrease = Math.min(deltaYPercent, maxDecrease);
          newHeight -= actualDecrease;
          newTop += actualDecrease;
        }
        if (direction === ResizeDirectionEnum.BOTTOM_LEFT || direction === ResizeDirectionEnum.BOTTOM_RIGHT) {
          const maxIncrease = 1 - newTop - newHeight;
          newHeight += Math.min(deltaYPercent, maxIncrease);
        }
      }

      // Constrain to parent bounds
      newWidth = Math.max(minimumWidthPercentage, Math.min(1 - newLeft, newWidth));
      newHeight = Math.max((minimumWidthPercentage / aspectRatio) * videoAspectRatio, Math.min(1 - newTop, newHeight));
      if (maintainAspectRatio) {
        if (newWidth > (newHeight * aspectRatio) / videoAspectRatio) {
          newWidth = (newHeight * aspectRatio) / videoAspectRatio;
        }
        if (newHeight > (newWidth * videoAspectRatio) / aspectRatio) {
          newHeight = (newWidth * videoAspectRatio) / aspectRatio;
        }
      }
      newLeft = Math.max(0, Math.min(1 - newWidth, newLeft));
      newTop = Math.max(0, Math.min(1 - newHeight, newTop));

      // Update the outline visually
      outlineRef.current.style.top = `${newTop * 100}%`;
      outlineRef.current.style.left = `${newLeft * 100}%`;
      outlineRef.current.style.width = `${newWidth * 100}%`;
      outlineRef.current.style.height = `${newHeight * 100}%`;

      maskRef.current?.setAttribute('x', `${newLeft * 100}%`);
      maskRef.current?.setAttribute('y', `${newTop * 100}%`);
      maskRef.current?.setAttribute('width', `${newWidth * 100}%`);
      maskRef.current?.setAttribute('height', `${newHeight * 100}%`);
    };

    const handleMouseUp = () => {
      const outline = outlineRef.current;
      if (!outline) {
        return;
      }

      // Get final positions from the outline styles
      const finalTop = parseFloat(outline.style.top);
      const finalLeft = parseFloat(outline.style.left);
      const finalWidth = parseFloat(outline.style.width);
      const finalHeight = parseFloat(outline.style.height);

      const finalPositions = {
        top: finalTop / 100,
        left: finalLeft / 100,
        width: finalWidth / 100,
        height: finalHeight / 100
      };

      updatePositions(finalPositions);

      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <>
      <svg className="absolute inset-0 size-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask id="crop-mask">
            <rect width="100%" height="100%" fill="white"></rect>
            <rect
              ref={maskRef}
              x={`${positions.left * 100}%`}
              y={`${positions.top * 100}%`}
              width={`${positions.width * 100}%`}
              height={`${positions.height * 100}%`}
              fill="black"
            ></rect>
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.5)" mask="url(#crop-mask)" rx="8" ry="8"></rect>
      </svg>
      {showPersonAndFaceCoordinates && facialBounds && (
        <div className="absolute inset-0 border-2 border-red-500">
          {Object.entries(facialBounds).map(([key, bound]) => (
            <div
              key={key}
              className="absolute"
              style={{
                border: '2px solid #FFF',
                left: `${bound.top_left.x * 100}%`,
                top: `${bound.top_left.y * 100}%`,
                width: `${(bound.bottom_right.x - bound.top_left.x) * 100}%`,
                height: `${(bound.bottom_right.y - bound.top_left.y) * 100}%`
              }}
            >
              <div className="absolute right-0 top-0 rounded-sm bg-black/50 px-1 py-0.5 text-3xs text-white">{key}</div>
            </div>
          ))}
        </div>
      )}
      {showPersonAndFaceCoordinates && personBounds && (
        <div className="absolute inset-0 border-2 border-blue-500">
          {Object.entries(personBounds).map(([key, bound]) => (
            <div
              key={key}
              className="absolute"
              style={{
                border: '2px solid #FFCB05',
                left: `${bound.top_left.x * 100}%`,
                top: `${bound.top_left.y * 100}%`,
                width: `${(bound.bottom_right.x - bound.top_left.x) * 100}%`,
                height: `${(bound.bottom_right.y - bound.top_left.y) * 100}%`
              }}
            >
              <div className="absolute right-0 top-0 rounded-sm bg-black/50 px-1 py-0.5 text-3xs text-white">{key}</div>
            </div>
          ))}
        </div>
      )}
      <div
        ref={outlineRef}
        className="border-2 border-deep-orange"
        style={{
          position: 'absolute',
          top: `${positions.top * 100}%`,
          left: `${positions.left * 100}%`,
          width: `${positions.width * 100}%`,
          height: `${positions.height * 100}%`,
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          pointerEvents: 'auto'
        }}
        onMouseDown={e => handleDragStart(e)}
      >
        {Object.values(ResizeDirectionEnum).map(direction => {
          return (
            <div
              key={direction}
              className="size-4 rounded-full border border-slate-300 bg-white"
              style={{
                position: 'absolute',
                backgroundColor: 'white',
                [direction.split('-')[0]]: '-8px',
                [direction.split('-')[1]]: '-8px',
                cursor: getCursor(direction)
              }}
              onMouseDown={e => handleResizeStart(e, direction)}
            />
          );
        })}
      </div>
    </>
  );
};
